'use client';

import { Text } from '@ironhack/design-system2/components/text';
import { find, pathEq } from 'ramda';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { Button } from '@/app/components/client/button';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { sendEvent } from '@/lib/gtm';
import { isMastersActive } from '@/lib/utils/masters';
import { type WebCohort } from '@/types';
import { Props as CohortSelectorProps } from './cohort-selector';
import { CohortSelection } from './components/cohort-selection';
import { CohortSelectorCarousel } from './components/cohort-selector-carousel';

type Props = CohortSelectorProps & { cohorts: WebCohort[] };

export const CohortSelectorClient = (props: Props): ReactElement | null => {
  const { data, cohorts, gaPageData, params, ...flexProps } = props;
  const {
    cohortSelection: [cohortSelection],
    emptyCohortsButton,
    highlightText,
    moduleFormat: [moduleFormat],
    subtitle,
    title,
  } = data;
  const { gaCategory, gaPageType } = gaPageData;
  const [selectedCohort, setSelectedCohort] = useState<WebCohort>();
  const [isAnimatingCohortSelection, setIsAnimatingCohortSelection] =
    useState<boolean>(false);
  const selectorRef = useRef<HTMLDivElement>();

  // Initialize selected cohort from sessionStorage
  useEffect(() => {
    const storedCohortRaw = sessionStorage.getItem('selectedCohort');
    try {
      const storedCohort = JSON.parse(storedCohortRaw as string) as WebCohort;
      if (cohorts.some((c) => c.id === storedCohort.id)) {
        setSelectedCohort(storedCohort);
      }
    } catch {}
  }, [cohorts]);

  const { isEnabled } = useFeatureToggle();
  if (isEnabled('pdp-hero-form')) return null;

  const handleCohortSelected = (cohortId: string) => {
    const newCohort = find(
      pathEq(['external_ids', 'salesforce'], cohortId),
      cohorts
    ) as WebCohort;
    sendEvent({
      eventCategory: gaCategory,
      eventAction: 'select cohort',
      eventLabel: `${newCohort.campus}::${newCohort.track}::${newCohort.format}`,
      pageType: gaPageType,
    });
    sessionStorage.setItem('selectedCohort', JSON.stringify(newCohort));
    setSelectedCohort(newCohort);

    // Trigger animation
    setIsAnimatingCohortSelection(true);
    setTimeout(() => setIsAnimatingCohortSelection(false), 400);
  };

  const onUnselectClick = () => {
    selectorRef.current?.scrollIntoView({ behavior: 'smooth' });
    sendEvent({
      eventAction: 'select sticky cohort',
      eventCategory: gaCategory,
      pageType: gaPageType,
    });
  };

  const onSelectedCohortClick = () => {
    const { campus, format, track } = selectedCohort as WebCohort;
    sendEvent({
      eventCategory: gaCategory,
      eventAction: 'click sticky apply now',
      eventLabel: `${campus}::${track}::${format}`,
      pageType: gaPageType,
    });
  };

  const cohortsExist = cohorts.length > 0;

  return (
    <>
      <ModuleWrapper
        innerProps={{ direction: 'column', gap: 3 }}
        moduleFormat={moduleFormat}
        outerProps={flexProps}
        ref={selectorRef}
      >
        {title && <Text textStyle={['3xl', null, '4xl']}>{title}</Text>}
        {highlightText && (
          <Text color="purple.100" textStyle="l">
            {highlightText}
          </Text>
        )}
        {subtitle && <Text textStyle="l">{subtitle}</Text>}
        {cohortsExist ? (
          <CohortSelectorCarousel
            cohorts={cohorts}
            gaPageData={gaPageData}
            // Hide tabs if Masters is active and there's only one (FT) tab
            hideSingleTab={isMastersActive(params.region)}
            params={params}
            radioGroupProps={{
              onChange: handleCohortSelected,
              value: selectedCohort?.external_ids?.salesforce,
            }}
          />
        ) : (
          <Button {...emptyCohortsButton} />
        )}
      </ModuleWrapper>
      {cohortsExist && (
        <CohortSelection
          cohort={selectedCohort}
          // Hide format if Masters is active
          hideFormat={isMastersActive(params.region)}
          isAnimating={isAnimatingCohortSelection}
          onSelectedClick={onSelectedCohortClick}
          onUnselectClick={onUnselectClick}
          {...cohortSelection}
        />
      )}
    </>
  );
};
